import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, BarChart3, Table2, Download, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

/**
 * Base Widget Component
 * Provides common functionality for all widgets:
 * - Loading states
 * - Error handling
 * - Chart/Table mode toggle
 * - Export functionality
 * 
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message
 * @param {ReactNode} chartComponent - Chart view component
 * @param {ReactNode} tableComponent - Table view component
 * @param {Function} onExport - Export handler
 * @param {string} defaultMode - Initial mode ('chart' | 'table')
 * @param {boolean} showToggle - Show chart/table toggle
 * @param {boolean} showExport - Show export button
 */
function WidgetBase({
    loading = false,
    error = null,
    chartComponent,
    tableComponent,
    onExport,
    defaultMode = 'chart',
    showToggle = true,
    showExport = true,
    emptyMessage = 'No data available'
}) {
    const [mode, setMode] = useState(defaultMode);

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-brand-accent mx-auto mb-3" />
                    <p className="text-sm text-brand-dark/70">Loading data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert variant="destructive" className="my-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                    {error}
                </AlertDescription>
            </Alert>
        );
    }

    // No data
    if (!chartComponent && !tableComponent) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-brand-dark/70">{emptyMessage}</p>
            </div>
        );
    }

    // Single mode (no toggle)
    if (!showToggle || (!chartComponent || !tableComponent)) {
        return (
            <div className="w-full">
                {showExport && onExport && (
                    <div className="flex justify-end mb-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onExport}
                            className="text-brand-dark border-brand-dark/20 hover:bg-brand-light-1"
                        >
                            <Download className="mr-2 h-4 w-4" />
                            Export CSV
                        </Button>
                    </div>
                )}
                <div className="w-full">
                    {chartComponent || tableComponent}
                </div>
            </div>
        );
    }

    // Dual mode with toggle
    return (
        <div className="w-full">
            <Tabs value={mode} onValueChange={setMode} className="w-full">
                {/* Controls Bar */}
                <div className="flex items-center justify-between mb-4">
                    <TabsList className="bg-brand-light-1">
                        <TabsTrigger
                            value="chart"
                            className="data-[state=active]:bg-brand-accent data-[state=active]:text-white"
                        >
                            <BarChart3 className="mr-2 h-4 w-4" />
                            Chart
                        </TabsTrigger>
                        <TabsTrigger
                            value="table"
                            className="data-[state=active]:bg-brand-accent data-[state=active]:text-white"
                        >
                            <Table2 className="mr-2 h-4 w-4" />
                            Table
                        </TabsTrigger>
                    </TabsList>

                    {showExport && onExport && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onExport}
                            className="text-brand-dark border-brand-dark/20 hover:bg-brand-light-1"
                        >
                            <Download className="mr-2 h-4 w-4" />
                            Export CSV
                        </Button>
                    )}
                </div>

                {/* Content */}
                <TabsContent value="chart" className="mt-0">
                    {chartComponent}
                </TabsContent>

                <TabsContent value="table" className="mt-0">
                    {tableComponent}
                </TabsContent>
            </Tabs>
        </div>
    );
}

export default WidgetBase;
